import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  Play,
  MessageSquare,
  TrendingUp,
  Award,
  Sparkles,
  Trophy,
  GraduationCap,
  Clock,
  BarChart3,
  Briefcase,
  Users,
} from 'lucide-react';

const AppShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });

  const sidebarItems = [
    { icon: Play, label: 'Practice', active: true },
    { icon: Trophy, label: 'Competitions' },
    { icon: GraduationCap, label: 'Academy' },
    { icon: TrendingUp, label: 'Progress' },
    { icon: Clock, label: 'History' },
    { icon: Award, label: 'Achievements' },
    { icon: BarChart3, label: 'Leaderboards' },
    { icon: Briefcase, label: 'Career' },
    { icon: Users, label: 'Groups' },
  ];

  const scenarios = [
    {
      title: 'MC Bliss Tray — Heritage Vineyards',
      sub: 'International Paper • Packaging Ops Supervisor',
      desc: 'Sell the MC Bliss Tray packaging solution to address stacking failures.',
    },
    {
      title: 'SaaS Discovery Call',
      sub: 'Acme Cloud • VP of Operations',
      desc: 'Run discovery and uncover pain around manual workflows.',
    },
    {
      title: 'Objection: Pricing Pushback',
      sub: 'Drill • Stay in the Ring',
      desc: 'Clarify, address, and confirm under pressure on price.',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 sm:py-32 relative overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 border border-primary/10 rounded-2xl rotate-12 animate-float" style={{ animationDelay: '0s' }} />
        <div className="absolute bottom-32 right-20 w-24 h-24 border border-accent/10 rounded-full animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-10 w-16 h-16 bg-electric-purple/5 rounded-lg rotate-45 animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Section Header */}
        <div className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-4">
            <Sparkles className="w-4 h-4 animate-pulse" />
            See It In Action
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Your{' '}
            <span className="relative">
              <span className="text-gradient bg-gradient-to-r from-primary via-accent to-electric-purple bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                Command Center
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                <path
                  d="M2 6C50 2 150 2 198 6"
                  stroke="url(#underline-gradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 200,
                    strokeDashoffset: isVisible ? 0 : 200,
                    transition: 'stroke-dashoffset 1s ease-out 0.5s',
                  }}
                />
                <defs>
                  <linearGradient id="underline-gradient" x1="0" y1="0" x2="200" y2="0">
                    <stop offset="0%" stopColor="hsl(var(--primary))" />
                    <stop offset="50%" stopColor="hsl(var(--accent))" />
                    <stop offset="100%" stopColor="hsl(260 70% 60%)" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Track every metric, crush every objection, and watch your scores skyrocket
          </p>
        </div>

        {/* Main Dashboard Mockup */}
        <div className={`relative transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`}>
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-electric-purple/20 blur-3xl opacity-50 scale-95 animate-pulse-glow" />

          {/* Browser frame */}
          <div className="relative glass rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 shadow-2xl hover:border-white/20 transition-colors duration-300">
            {/* Browser header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-background/50 border-b border-white/10">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-lg bg-muted/30 text-xs text-muted-foreground font-mono flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                  bigesales.com/dashboard
                </div>
              </div>
            </div>

            {/* Dashboard body: sidebar + content */}
            <div className="flex bg-background/80 min-h-[420px]">
              {/* Sidebar */}
              <aside className="w-14 sm:w-16 border-r border-white/10 bg-background/40 py-3 flex flex-col items-center gap-1 shrink-0">
                <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center mb-2 animate-pulse-glow">
                  <span className="font-display font-bold text-primary text-sm">E</span>
                </div>
                {sidebarItems.map((item) => (
                  <div
                    key={item.label}
                    title={item.label}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                      item.active
                        ? 'bg-primary/15 text-electric-blue'
                        : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                  </div>
                ))}
              </aside>

              {/* Content */}
              <div className="flex-1 min-w-0 flex flex-col">
                {/* Header bar */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-foreground text-sm sm:text-base">
                      <span className="text-primary">big</span>
                      <span className="text-foreground">E</span>
                      <span className="text-muted-foreground font-normal"> sales coach</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/40 to-electric-purple/40 border border-white/10" />
                  </div>
                </div>

                {/* Main content */}
                <div className="p-4 sm:p-6 flex-1">
                  {/* Greeting */}
                  <div className="mb-5">
                    <h3 className="font-display text-lg sm:text-2xl font-bold text-foreground">
                      Good afternoon, Alex!
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      You're on a 12-day streak — keep it up! 🔥
                    </p>
                  </div>

                  {/* Scenario grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {scenarios.map((s, i) => (
                      <div
                        key={s.title}
                        className="relative glass rounded-xl p-4 border border-primary/20 overflow-hidden group hover:border-primary/40 transition-colors"
                        style={{
                          transitionDelay: `${i * 80}ms`,
                          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                          opacity: isVisible ? 1 : 0,
                        }}
                      >
                        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
                        <div className="relative">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-semibold text-foreground text-sm leading-snug">{s.title}</p>
                            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/30 transition-colors">
                              <Play className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{s.sub}</p>
                          <p className="text-xs text-muted-foreground/70 leading-relaxed line-clamp-2">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature highlights with stagger */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Track your improvement across all rubric categories' },
            { icon: Award, title: 'Achievement System', desc: 'Earn badges and maintain practice streaks' },
            { icon: MessageSquare, title: 'AI Coaching', desc: 'Get personalized tips based on your performance' },
          ].map((item, index) => (
            <div
              key={item.title}
              className={`glass rounded-xl p-4 text-center transition-all duration-700 hover:scale-105 hover:bg-foreground/5 group cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${600 + index * 150}ms` }}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <item.icon className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
              </div>
              <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
